import axios from 'axios'
import firebase from 'firebase/compat/app'
import jenks from 'turf-jenks'
import router from '../router'
const fb = require('../firebaseConfig.js')
const { buildFilterExpression } = require('./filterExpression')
const { deriveHeaders, mergeHeaders } = require('../lib/deriveHeaders')

// Guard for visualizeLayer's lazy GeoJSON load (see below). Tracks the path
// currently being fetched / already attempted so we never fire duplicate
// fetches or loop forever when a fetch fails. Module-level (not Vuex state) so
// it never triggers reactivity.
let geoJsonLazyLoad = { path: null, status: 'idle' }

// Live-tracking listener registry (Phase 1 — docs/live-tracking-phase1.md).
// Module-level, not Vuex state: listeners and per-track segment caches never
// need reactivity, and detach/re-attach must not churn the store.
let liveTracksUnsub = null   // tracks-query listener (farmId + inProgress)
let liveLinesUnsubs = {}     // trackId -> lines-subcollection listener
let liveTracksData = {}      // trackId -> { label, startedAt, segments, lastPointAt }
let liveCountUnsub = null    // header-pill count-only listener
let liveRenderTimer = null   // >=1s render throttle (chunk writes burst)

export default {
  async launchDataset ({ commit, state, dispatch }, options) {
    let dataset = options.dataset
    let collecting = false
    if (options.collecting) {
      collecting = true
    }
    // Patch default vizSettings BEFORE the map sees the dataset.
    //
    // Previously this patch only happened when DatasetVizSettings.vue
    // mounted (its created() hook fills in missing keys against a
    // `defaultViz` object). The drawer doesn't mount automatically on
    // first load, so the map's `visualizeLayer` action — which dispatches
    // ~1s after the geojson is fetched — read undefined values for
    // circleRadius / circleRadiusMin / circleRadiusMax and Mapbox
    // silently treated `setPaintProperty('circle-radius', undefined)` as
    // a no-op. A page reload fixed it because by then the user had
    // opened the drawer and the defaults had been merged into the
    // Firestore doc. This patch makes the defaults present from the
    // moment the dataset enters state.
    if (dataset && typeof dataset === 'object') {
      const datasetVizDefaults = {
        type: 'circle',
        lineWidth: 2,
        opacity: 1,
        colorBy: 'solid',
        circleColor: '#ffc107',
        circleRadius: 4,
        circleRadiusMin: 5,
        circleRadiusMax: 20,
        colorHeader: false,
        featureCategories: [],
        filters: [],
        labelBy: 'noLabel',
        labelHeader: false,
        // Shared label styling (points / lines / polygons). Previously the
        // label symbol layer hard-coded white text at the default size with
        // no UI control; these make colour + size user-adjustable.
        labelColor: '#ffffff',
        labelSize: 14,
        // Polygon outline. The fill layer's hard-coded white
        // `fill-outline-color` can't be widthed, so a dedicated border line
        // layer reads these (same approach the farm blocks outline uses).
        borderColor: '#ffffff',
        borderWidth: 1
      }
      // REACTIVITY — the root cause of "the viz panel does nothing until I
      // refresh" on a dataset's FIRST open after creation. The dataset object
      // arriving here is often ALREADY observed by Vue (CreateDataset commits
      // it to the store before launching), and Vue 2 cannot see a key that is
      // added to an observed object by plain assignment. Attaching
      // `dataset.vizSettings = {...}` in place therefore produced a
      // non-reactive vizSettings: clicking Value Based changed colorBy in the
      // data, but the template never re-rendered (confirmed live:
      // `vizSettings.__ob__` was undefined). After a refresh the doc arrives
      // with vizSettings already present, gets observed as a whole, and
      // "works the second time" — the classic symptom. So: build a NEW
      // vizSettings and a NEW dataset object and commit that; a fresh plain
      // object is observed deeply on assignment to state.
      const patchedViz = { ...(dataset.vizSettings || {}) }
      Object.keys(datasetVizDefaults).forEach(k => {
        const v = patchedViz[k]
        // Match DatasetVizSettings.vue's truthiness rule: any
        // genuinely-missing value gets defaulted; literal `false`
        // (e.g. colorHeader: false) is preserved. Numeric values are
        // also preserved as-is so a deliberate 0 (e.g. borderWidth: 0
        // meaning "no border") survives a reopen instead of snapping
        // back to the default.
        if (v === undefined || (!v && v !== false && typeof v !== 'number')) {
          patchedViz[k] = datasetVizDefaults[k]
        }
      })
      dataset = { ...dataset, vizSettings: patchedViz }
    }
    // Set dataset in store
    commit('setStateProperty', { property: 'dataset', value: dataset })
    // Setup Route
    if (router.currentRoute.params.layerId !== dataset.id) {
      router.push({
        name: 'Farm',
        params: {
          id: state.farm.id,
          layerType: 'dataset',
          layerId: state.dataset.id
        }
      })
    }
    if (!options.collecting) {
      // Tuck Datasets Drawer
      commit('tuckDrawer', 'datasets')
      commit('tuckDrawer', 'datasetDetails')
      commit('hideDrawer', 'createDataset')
    }
    // Remove all map layers
    await dispatch('removeAllLayers')
    // If not dynamic
    if (!dataset.dynamic) {
      // Map the layer
      await dispatch('mapDataset')
      // Open the viz settings
      if (!collecting) {
        commit('showDrawer', 'datasetDetails')
      }
    } else if (dataset.dynamic) {
    // Else if dynamic
    //   check to see if new version exists. If not
    //     Map the layer
    //     Open the viz settings
    //   if new version does exist
    //     NEED TO LOOK INTO THIS
      fb.collectorsCollection.doc(dataset.collectorId).get().then(async collectorDoc => {
        const collectorData = collectorDoc.data()
        collectorData.id = collectorDoc.id
        collectorData.collecting = collecting
        // Preserve draw control and polygon state when user is collecting (e.g. polygon drawer open)
        const prev = state.collector
        if (prev.collecting && prev.collectorDrawControl) {
          collectorData.collectorDrawControl = prev.collectorDrawControl
          collectorData.drawCreateHandler = prev.drawCreateHandler
          collectorData.selectedGeometry = prev.selectedGeometry
          collectorData.selectedLocation = prev.selectedLocation
        }
        commit('setStateProperty', { property: 'collector', value: collectorData })
        const collectorUpdated = collectorData.updatedOn.toDate().getTime()
        const dataSetUpdated = dataset.updatedOn.toDate().getTime()
        if (!collecting && (collectorUpdated > dataSetUpdated || options.recompile || collectorData.reCompile)) {
          console.log('New version available... processing')
          commit('setAlert', { active: true, message: 'There is new data available in this dataset. Compiling now...', level: 'alert-info' })
          fb.compileRequestsCollection.add({
            createdBy: state.currentUser.uid,
            createdOn: new Date(),
            collectorId: collectorData.id,
            datasetId: dataset.id,
            lastUpdated: dataset.updatedOn,
            farmId: state.farm.id,
            complete: false
          }).then(docRef => {
            console.log('Compilation request added')
            // Subscribe to changes
            const unsubscribe = docRef.onSnapshot(docSnapshot => {
              const data = docSnapshot.data()
              if (data.complete) {
                // Remove alert
                commit('setAlert', { active: false })
                // Get the updated dataset doc
                fb.datasetsCollection.doc(dataset.id).get().then(datasetDoc => {
                  const newDataset = datasetDoc.data()
                  newDataset.id = datasetDoc.id
                  // Set new dataset
                  commit('setStateProperty', { property: 'dataset', value: newDataset })
                  // Relaunch dataset
                  dispatch('launchDataset', { dataset: newDataset, collecting: collecting })
                })
                unsubscribe()
              }
            })

            return fb.datasetsCollection.doc(dataset.id).update({
              updatedOn: new Date()
            }).then(() => {
              console.log('Updated data on dataset')
            })
          }).catch(err => {
            console.log(err)
          })
        } else if (!dataset.file.geoJsonPath) {
          if (!collecting) {
            console.log('No file or data!')
            commit('setAlert', { active: true, message: 'No data in this dataset. You must collect data before viewing.', level: 'alert-warning' })
            return false
          }
        }
        await dispatch('mapDataset')
        if (!collecting) {
          commit('showDrawer', 'datasetDetails')
        }
      }).catch(err => { console.log(err) })
    }
  },
  async mapDataset ({ commit, state, dispatch }) {
    const dataset = state.dataset
    commit('setMapLoader', true)
    // Save blocks layer visibility state
    // const showBlocks = dataset.vizSettings && dataset.vizSettings.showBlocks
    // Default to satellite if no baseLayer specified
    const baseLayer = dataset.vizSettings?.baseLayer || 'satellite'
    // BASE LAYER AND BLOCKS LAYER
    if (baseLayer === 'light') {
      state.mapObject.setStyle('mapbox://styles/lergp/ckvs59r650pxk14p6c9m507ne')
      await new Promise((resolve) => {
        const checkStyleLoaded = () => {
          if (state.mapObject.isStyleLoaded()) {
            // if (showBlocks) {
            //   dispatch('addBlocksLayer')
            // }
            // Custom raster base layer (if farm has one configured) goes on
            // FIRST so the blocks layer added next sits on top of it.
            dispatch('addCustomBaseLayer').finally(() => {
              dispatch('addBlocksLayer')
            })
            resolve()
          } else {
            setTimeout(checkStyleLoaded, 100)
          }
        }
        setTimeout(() => {
          checkStyleLoaded()
        }, 1000)
      })
    } else if (baseLayer === 'dark') {
      state.mapObject.setStyle('mapbox://styles/lergp/ckvs5dko20hc414sc535q7sn9')
      await new Promise((resolve) => {
        const checkStyleLoaded = () => {
          if (state.mapObject.isStyleLoaded()) {
            // if (showBlocks) {
            //   dispatch('addBlocksLayer')
            // }
            // Custom raster base layer (if farm has one configured) goes on
            // FIRST so the blocks layer added next sits on top of it.
            dispatch('addCustomBaseLayer').finally(() => {
              dispatch('addBlocksLayer')
            })
            resolve()
          } else {
            setTimeout(checkStyleLoaded, 100)
          }
        }
        setTimeout(() => {
          checkStyleLoaded()
        }, 1000)
      })
    } else {
      state.mapObject.setStyle('https://api.maptiler.com/maps/da520b6d-739a-4646-888f-1d3f2b664f78/style.json?key=IuJ0D0R9S0Cjl4krhvr1')
      await new Promise((resolve) => {
        const checkStyleLoaded = () => {
          if (state.mapObject.isStyleLoaded()) {
            // if (showBlocks) {
            //   dispatch('addBlocksLayer')
            // }
            // Custom raster base layer (if farm has one configured) goes on
            // FIRST so the blocks layer added next sits on top of it.
            dispatch('addCustomBaseLayer').finally(() => {
              dispatch('addBlocksLayer')
            })
            resolve()
          } else {
            setTimeout(checkStyleLoaded, 100)
          }
        }
        setTimeout(() => {
          checkStyleLoaded()
        }, 1000)
      })
    }
    // SUBLAYER
    if (dataset.vizSettings && dataset.vizSettings.subLayer) {
      // Have to go get the latest file path in case the geojson file has been updated and replaced
      const subLayerDoc = await fb.datasetsCollection.doc(dataset.vizSettings.subLayer.id).get()
      const subLayerData = subLayerDoc.data()
      subLayerData.id = subLayerDoc.id
      // Get the geojson
      const subLayerGeoJsonPath = subLayerData.file.geoJsonPath
      const subLayerStorageRef = firebase.storage().ref(subLayerGeoJsonPath)
      const subLayerFileUrl = await subLayerStorageRef.getDownloadURL()
      // Add the Source
      state.mapObject.addSource('subLayerSource', {
        type: 'geojson',
        data: subLayerFileUrl
      })
      // Add the Layer
      state.mapObject.addLayer({
        id: 'subLayer',
        type: 'circle',
        source: 'subLayerSource',
        layout: {},
        paint: {
          'circle-radius': 10,
          'circle-color': '#ffc107'
        }
      })
      state.mapObject.addLayer({
        id: 'subLayerLine',
        type: 'line',
        source: 'subLayerSource',
        layout: { visibility: 'none' },
        paint: {
          'line-color': '#ffc107',
          'line-width': subLayerData.vizSettings?.lineWidth ?? 2,
          'line-opacity': 0.8
        }
      })
      const response = await axios.get(subLayerFileUrl)
      commit('setStateProperty', { property: 'subLayerGeoJson', value: response.data })
      // VISUALIZE LAYER
      setTimeout(() => {
        dispatch('visualizeLayer', 'subLayer')
      }, 1000)
    }
    // DATASET LAYER
    // CLICK ON DATAPOINTS
    function onClickFunction (e) {
      // state.avirisSampling — the AVIRIS-3 tab owns the map click while
      // sampling, so clicking a dataset point reads its spectrum rather
      // than opening the datapoint drawer.
      if (state.avirisSampling || state.drawer.copyDataset.position === 'in' || state.collector.collecting || state.datapointSelectionMode) {
        return false
      } else {
        // HIGHLIGHT THE DATAPOINT (query only layers that exist so we don't throw on missing polygon/line)
        const datasetLayerIds = ['datasetPreview', 'datasetPreviewPolygon', 'datasetPreviewLine', 'subLayer', 'subLayerPolygon', 'subLayerLine']
        const existingLayers = datasetLayerIds.filter(id => state.mapObject.getLayer(id))
        const features = state.mapObject.queryRenderedFeatures(e.point, { layers: existingLayers })
        if (!features.length) { return }
        // Remove previous selection (layers first, then source) to match block highlight flow
        if (state.mapObject.getLayer('selectedDataPoint')) {
          state.mapObject.removeLayer('selectedDataPoint')
        }
        if (state.mapObject.getLayer('selectedDataPointHighlight')) {
          state.mapObject.removeLayer('selectedDataPointHighlight')
        }
        if (state.mapObject.getLayer('selectedDataPointPolygon')) {
          state.mapObject.removeLayer('selectedDataPointPolygon')
        }
        if (state.mapObject.getLayer('selectedDataPointLine')) {
          state.mapObject.removeLayer('selectedDataPointLine')
        }
        if (state.mapObject.getSource('selectedDataPointPolygon')) {
          state.mapObject.removeSource('selectedDataPointPolygon')
        }
        if (state.mapObject.getSource('selectedDataPoint')) {
          state.mapObject.removeSource('selectedDataPoint')
        }
        const feature = features[0]
        const geomType = feature.geometry && feature.geometry.type
        if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
          // Match block highlight: single fill layer with inline source (amber overlay)
          state.mapObject.addLayer({
            id: 'selectedDataPointPolygon',
            type: 'fill',
            source: {
              type: 'geojson',
              data: feature.toJSON()
            },
            layout: {},
            paint: {
              'fill-color': '#ffc107',
              'fill-opacity': 0.8
            }
          })
        } else if (geomType === 'LineString' || geomType === 'MultiLineString') {
          state.mapObject.addSource('selectedDataPoint', {
            type: 'geojson',
            data: feature.toJSON()
          })
          state.mapObject.addLayer({
            id: 'selectedDataPointLine',
            type: 'line',
            source: 'selectedDataPoint',
            paint: {
              'line-width': (state.dataset.vizSettings?.lineWidth || 2) + 2,
              'line-color': '#ffc107',
              'line-opacity': 0.95
            }
          })
        } else {
          state.mapObject.addSource('selectedDataPoint', {
            type: 'geojson',
            data: feature.toJSON()
          })
          state.mapObject.addLayer({
            id: 'selectedDataPointHighlight',
            type: 'circle',
            source: 'selectedDataPoint',
            paint: {
              'circle-radius': state.dataset.vizSettings.circleRadius + 4,
              'circle-color': 'white'
            }
          })
          state.mapObject.addLayer({
            id: 'selectedDataPoint',
            type: 'circle',
            source: 'selectedDataPoint',
            paint: {
              'circle-radius': state.dataset.vizSettings.circleRadius + 2,
              'circle-color': 'red'
            }
          })
        }
        const dataPointId = e.features[0].properties.dataPointId
        // PUT AWAY ANY DATAPOINT DRAWERS
        // commit('setDrawer', { name: 'dataPointDetails', position: 'out' })
        commit('tuckDrawer', 'datasetDetails')
        commit('hideDrawer', 'dataPointDetails')
        commit('hideDrawer', 'datasets')
        commit('hideDrawer', 'revisions')
        commit('hideDrawer', 'previewRevision')
        commit('hideDrawer', 'farmDetails')
        commit('hideDrawer', 'collectors')
        setTimeout(() => {
          // HANDLE DYNAMIC DATASETS
          if (state.dataset.dynamic) {
            fb.collectorsCollection.doc(state.dataset.collectorId).collection('dataPoints').doc(dataPointId).get().then(doc => {
              const dataPoint = doc.data()
              dataPoint.id = doc.id
              return commit('setStateProperty', { property: 'dataPoint', value: dataPoint })
            }).then(() => {
              commit('showDrawer', 'dataPointDetails')
            })
          } else {
            // HANDLE STATIC DATASETS
            const dataPoint = features[0].properties
            // LIST OUT PROPERTIES ARRAY IN DATAPOINT
            dataPoint.fields = Object.keys(dataPoint)
            dataPoint.coordinates = features[0].geometry.coordinates
            commit('setStateProperty', { property: 'dataPoint', value: dataPoint })
            commit('showDrawer', 'dataPointDetails')
          }
        }, 300)
      }
    }
    state.mapObject.off('click', 'datasetPreview', onClickFunction)
    state.mapObject.off('click', 'datasetPreviewLine', onClickFunction)
    state.mapObject.off('click', 'datasetPreviewPolygon', onClickFunction)
    // Add GeoJSON dataset preview
    if (dataset.file.geoJsonPath) {
      const geoJsonPath = dataset.file.geoJsonPath
      const geoJsonStorageRef = firebase.storage().ref(geoJsonPath)
      const geoFileUrl = await geoJsonStorageRef.getDownloadURL()
      const useCollectingSource = dataset.dynamic && state.collector.collecting
      let geoJsonData
      if (useCollectingSource) {
        const response = await axios.get(geoFileUrl)
        geoJsonData = response.data
        commit('setStateProperty', { property: 'datasetGeoJson', value: geoJsonData })
      }
      state.mapObject.addSource('datasetPreviewGeojson', {
        type: 'geojson',
        data: useCollectingSource ? geoJsonData : geoFileUrl
      })
      let circlePaint = { 'circle-radius': state.defaults.pointStops, 'circle-color': '#ffc107' }
      if (state.dataset.dynamic) {
        circlePaint = { 'circle-radius': 10, 'circle-color': '#ffc107' }
      }
      state.mapObject.addLayer({
        id: 'datasetPreview',
        type: 'circle',
        source: 'datasetPreviewGeojson',
        layout: {},
        paint: circlePaint
      })
      state.mapObject.addLayer({
        id: 'datasetPreviewLine',
        type: 'line',
        source: 'datasetPreviewGeojson',
        layout: { visibility: 'none' },
        paint: {
          'line-color': '#ffc107',
          'line-width': state.dataset.vizSettings?.lineWidth ?? 2,
          'line-opacity': 0.8
        }
      })
      // Establish the click function
      state.mapObject.on('click', 'datasetPreview', onClickFunction)
      state.mapObject.on('click', 'datasetPreviewLine', onClickFunction)
      state.mapObject.on('click', 'datasetPreviewPolygon', onClickFunction)
      state.mapObject.on('mouseenter', 'datasetPreview', function () {
        state.mapObject.getCanvas().style.cursor = 'pointer'
      })
      state.mapObject.on('mouseleave', 'datasetPreview', function () {
        state.mapObject.getCanvas().style.cursor = ''
      })
      state.mapObject.on('mouseenter', 'datasetPreviewLine', function () {
        state.mapObject.getCanvas().style.cursor = 'pointer'
      })
      state.mapObject.on('mouseleave', 'datasetPreviewLine', function () {
        state.mapObject.getCanvas().style.cursor = ''
      })
      state.mapObject.on('mouseenter', 'datasetPreviewPolygon', function () {
        state.mapObject.getCanvas().style.cursor = 'pointer'
      })
      state.mapObject.on('mouseleave', 'datasetPreviewPolygon', function () {
        state.mapObject.getCanvas().style.cursor = ''
      })
      if (!useCollectingSource) {
        const response = await axios.get(geoFileUrl)
        geoJsonData = response.data
        commit('setStateProperty', { property: 'datasetGeoJson', value: geoJsonData })
      }
      // Headers backfill. `dataset.headers` (the GeoJSON property field names
      // that drive color/size/label "by header" controls) is written at
      // creation from a client-side parse that runs async — so creating a
      // dataset before that parse resolves persists `headers: []`, and the
      // viz controls stay empty until the dataset is re-opened. Now that we
      // have the actual GeoJSON in hand, derive the headers from it and fill
      // them in whenever they're missing, so the controls work immediately on
      // first launch. Backfill the Firestore doc too (best-effort) so the
      // stored record is correct for next time.
      // Union across ALL features (shared deriveHeaders), and backfill
      // whenever the stored list is missing OR incomplete — never shrink it.
      if (!state.dataset.dynamic && geoJsonData && geoJsonData.features && geoJsonData.features.length) {
        const existingHeaders = Array.isArray(state.dataset.headers) ? state.dataset.headers : []
        const derivedHeaders = mergeHeaders(existingHeaders, deriveHeaders(geoJsonData))
        if (derivedHeaders.length > existingHeaders.length) {
          const updatedDataset = { ...state.dataset, headers: derivedHeaders }
          commit('setStateProperty', { property: 'dataset', value: updatedDataset })
          if (state.dataset.id) {
            fb.datasetsCollection.doc(state.dataset.id).update({ headers: derivedHeaders }).catch(err => {
              console.log('Headers backfill failed (non-fatal):', err)
            })
          }
        }
      }
      if (!state.dataset.vizSettings || !state.dataset.vizSettings.type) { // {} (API-created) counts as missing
        const geom = geoJsonData.features && geoJsonData.features[0] && geoJsonData.features[0].geometry
        const geomType = geom ? geom.type : 'Point'
        let vizType = 'circle'
        if (geomType === 'LineString' || geomType === 'MultiLineString') vizType = 'line'
        if (geomType === 'Polygon' || geomType === 'MultiPolygon') vizType = 'polygon'
        if (vizType === 'circle' && dataset.dynamic && state.collector.collecting) {
          if (state.collector.geometryType === 'polygon' || dataset.geometryType === 'polygon') vizType = 'polygon'
          if (state.collector.geometryType === 'line' || dataset.geometryType === 'line') vizType = 'line'
        }
        const defaultViz = {
          type: vizType,
          lineWidth: 2,
          opacity: 1,
          colorBy: 'solid',
          circleColor: '#ffc107',
          colorHeader: false,
          featureCategories: [],
          filters: [],
          labelBy: 'noLabel',
          labelHeader: false
        }
        commit('setStateProperty', { property: 'dataset', value: { ...state.dataset, vizSettings: defaultViz } })
      }
      if (!state.pauseMap && geoJsonData.features && geoJsonData.features.length) {
        setTimeout(() => {
          state.mapObject.fitBounds(turf.bbox(geoJsonData), { // eslint-disable-line
            animate: true,
            padding: 20,
            duration: 490
          })
        }, 1000)
      }
      // VISUALIZE LAYER
      setTimeout(() => {
        dispatch('visualizeLayer', 'datasetPreview')
      }, 1000)
    } else if (dataset.dynamic && state.collector.collecting) {
      const emptyGeoJson = { type: 'FeatureCollection', features: [] }
      commit('setStateProperty', { property: 'datasetGeoJson', value: emptyGeoJson })
      state.mapObject.addSource('datasetPreviewGeojson', {
        type: 'geojson',
        data: emptyGeoJson
      })
      const collectingGeometryType = state.collector.geometryType || dataset.geometryType
      const isPolygonCollector = collectingGeometryType === 'polygon'
      const isLineCollector = collectingGeometryType === 'line'
      const circlePaint = { 'circle-radius': 10, 'circle-color': '#ffc107' }
      state.mapObject.addLayer({
        id: 'datasetPreview',
        type: 'circle',
        source: 'datasetPreviewGeojson',
        layout: { visibility: (isPolygonCollector || isLineCollector) ? 'none' : 'visible' },
        paint: circlePaint
      })
      state.mapObject.addLayer({
        id: 'datasetPreviewLine',
        type: 'line',
        source: 'datasetPreviewGeojson',
        layout: { visibility: isLineCollector ? 'visible' : 'none' },
        paint: {
          'line-color': '#ffc107',
          'line-width': state.dataset.vizSettings?.lineWidth ?? 2,
          'line-opacity': 0.8
        }
      })
      if (isPolygonCollector) {
        state.mapObject.addLayer({
          id: 'datasetPreviewPolygon',
          type: 'fill',
          source: 'datasetPreviewGeojson',
          paint: {
            'fill-color': '#ffc107',
            'fill-opacity': 0.8,
            'fill-outline-color': 'white'
          }
        })
      }
      state.mapObject.on('click', 'datasetPreview', onClickFunction)
      state.mapObject.on('click', 'datasetPreviewLine', onClickFunction)
      state.mapObject.on('click', 'datasetPreviewPolygon', onClickFunction)
      state.mapObject.on('mouseenter', 'datasetPreview', function () {
        state.mapObject.getCanvas().style.cursor = 'pointer'
      })
      state.mapObject.on('mouseleave', 'datasetPreview', function () {
        state.mapObject.getCanvas().style.cursor = ''
      })
      state.mapObject.on('mouseenter', 'datasetPreviewLine', function () {
        state.mapObject.getCanvas().style.cursor = 'pointer'
      })
      state.mapObject.on('mouseleave', 'datasetPreviewLine', function () {
        state.mapObject.getCanvas().style.cursor = ''
      })
      state.mapObject.on('mouseenter', 'datasetPreviewPolygon', function () {
        state.mapObject.getCanvas().style.cursor = 'pointer'
      })
      state.mapObject.on('mouseleave', 'datasetPreviewPolygon', function () {
        state.mapObject.getCanvas().style.cursor = ''
      })
      if (!state.dataset.vizSettings || !state.dataset.vizSettings.type) { // {} (API-created) counts as missing
        const defaultViz = {
          type: isPolygonCollector ? 'polygon' : (isLineCollector ? 'line' : 'circle'),
          lineWidth: 2,
          opacity: 1,
          colorBy: 'solid',
          circleColor: '#ffc107',
          colorHeader: false,
          featureCategories: [],
          filters: [],
          labelBy: 'noLabel',
          labelHeader: false
        }
        commit('setStateProperty', { property: 'dataset', value: { ...state.dataset, vizSettings: defaultViz } })
      }
      setTimeout(() => {
        dispatch('visualizeLayer', 'datasetPreview')
      }, 1000)
    }
    // HANDLE RASTER DATASETS
    if (dataset.file.raster) {
      console.log('This is a Raster')
      // Go get the download URL for jpg file
      const jpgPath = dataset.file.jpgPath
      const jpgStorageRef = firebase.storage().ref(jpgPath)
      state.mapObject.fitBounds([
        [dataset.file.xMin, dataset.file.yMin],
        [dataset.file.xMax, dataset.file.yMax]
      ], {
        animate: false
      })
      const fileUrl = await jpgStorageRef.getDownloadURL()
      state.mapObject.addSource('rasterSource', {
        type: 'image',
        url: fileUrl,
        coordinates: [
          [dataset.file.xMin, dataset.file.yMax], // top left
          [dataset.file.xMax, dataset.file.yMax], // top right
          [dataset.file.xMax, dataset.file.yMin], // bottom right
          [dataset.file.xMin, dataset.file.yMin] // bottom left
        ]
      })
      state.mapObject.addLayer({
        id: 'rasterLayer',
        source: 'rasterSource',
        type: 'raster',
        paint: {
          'raster-opacity': 0.85
        }
      })
      commit('setStateProperty', { property: 'loadingMap', value: false })
    }
    // STOP SPINNER
    commit('setMapLoader', false)
  },
  visualizeLayer ({ commit, state, dispatch }, layerName) {
    let datasetRoot
    let geoJsonRoot
    let updatedDataset = state.dataset
    if (layerName === 'subLayer') {
      datasetRoot = state.dataset.vizSettings && state.dataset.vizSettings.subLayer
      geoJsonRoot = state.subLayerGeoJson
    } else {
      datasetRoot = state.dataset
      geoJsonRoot = state.datasetGeoJson
    }
    if (!datasetRoot || !datasetRoot.vizSettings) {
      return
    }
    // BULLETPROOF value-based rendering: color/size/label "by header" all read
    // features out of `geoJsonRoot` below (jenks breaks, category scans, etc.).
    // On a cold first load `state.datasetGeoJson` can still be {} because the
    // dataset launch fetches the GeoJSON file with a slow awaited axios.get —
    // so any value-based render that runs in that window silently produced no
    // coloring, and only worked after the dataset was opened a second time
    // (warm cache). If we need feature data and don't have it yet, lazily load
    // the file once, commit it, then re-run this exact render.
    if (layerName !== 'subLayer') {
      const vz = datasetRoot.vizSettings
      const needsFeatureData =
        (vz.colorBy === 'valueBased' && vz.colorHeader) ||
        vz.sizeBy === 'valueBased' ||
        (vz.labelBy && vz.labelBy !== 'noLabel' && vz.labelHeader)
      const haveFeatures = geoJsonRoot && Array.isArray(geoJsonRoot.features) && geoJsonRoot.features.length > 0
      const path = state.dataset.file && state.dataset.file.geoJsonPath
      const isRaster = state.dataset.file && state.dataset.file.raster
      if (needsFeatureData && !haveFeatures && path && !isRaster) {
        // Already attempted (succeeded or failed) for this exact path — don't
        // loop; fall through and render with what we have.
        const settledForPath = geoJsonLazyLoad.path === path &&
          (geoJsonLazyLoad.status === 'done' || geoJsonLazyLoad.status === 'error')
        if (!settledForPath) {
          if (!(geoJsonLazyLoad.path === path && geoJsonLazyLoad.status === 'loading')) {
            geoJsonLazyLoad = { path, status: 'loading' }
            firebase.storage().ref(path).getDownloadURL()
              .then(url => axios.get(url))
              .then(resp => {
                geoJsonLazyLoad.status = 'done'
                commit('setStateProperty', { property: 'datasetGeoJson', value: resp.data })
                dispatch('visualizeLayer', layerName)
              })
              .catch(e => {
                geoJsonLazyLoad.status = 'error'
                console.log('visualizeLayer: lazy GeoJSON load failed:', e && e.message)
              })
          }
          return
        }
      }
    }
    commit('setStateProperty', { property: 'showRampSettings', value: false })
    if (state.mapObject.getLayer('datasetPreviewHeatMap')) {
      state.mapObject.removeLayer('datasetPreviewHeatMap')
    }
    // Remove only the polygon layer corresponding to the current layerName
    const polygonLayerIdToRemove = layerName === 'subLayer' ? 'subLayerPolygon' : 'datasetPreviewPolygon'
    if (state.mapObject.getLayer(polygonLayerIdToRemove)) {
      state.mapObject.removeLayer(polygonLayerIdToRemove)
    }
    const colorHeader = datasetRoot.vizSettings.colorHeader
    let radius
    let color
    // Reset featureCategories
    datasetRoot.vizSettings.featureCategories = []
    // What type of Viz
    if (datasetRoot.vizSettings.type === 'circle' || datasetRoot.vizSettings.type === 'polygon' || datasetRoot.vizSettings.type === 'line') {
      // Determine polygon/line layer IDs and sources based on whether this is a sublayer
      const polygonLayerId = layerName === 'subLayer' ? 'subLayerPolygon' : 'datasetPreviewPolygon'
      const polygonSource = layerName === 'subLayer' ? 'subLayerSource' : 'datasetPreviewGeojson'
      const polygonBorderId = layerName === 'subLayer' ? 'subLayerPolygonBorder' : 'datasetPreviewPolygonBorder'
      const lineLayerId = layerName === 'subLayer' ? 'subLayerLine' : 'datasetPreviewLine'
      const lineSource = layerName === 'subLayer' ? 'subLayerSource' : 'datasetPreviewGeojson'
      // The polygon border is its own line layer (a fill layer's
      // `fill-outline-color` can't be widthed). Tear it down on every pass so
      // the non-polygon branches don't leave a stale outline behind.
      if (state.mapObject.getLayer(polygonBorderId)) {
        state.mapObject.removeLayer(polygonBorderId)
      }

      if (datasetRoot.vizSettings.type === 'circle') {
        state.mapObject.setLayoutProperty(layerName, 'visibility', 'visible')
        state.mapObject.setPaintProperty(layerName, 'circle-opacity', datasetRoot.vizSettings.opacity || 1)
        if (state.mapObject.getLayer(lineLayerId)) {
          state.mapObject.setLayoutProperty(lineLayerId, 'visibility', 'none')
        }
      } else if (datasetRoot.vizSettings.type === 'polygon') {
        state.mapObject.setLayoutProperty(layerName, 'visibility', 'none')
        if (state.mapObject.getLayer(lineLayerId)) {
          state.mapObject.setLayoutProperty(lineLayerId, 'visibility', 'none')
        }
        if (state.mapObject.getLayer(polygonLayerId)) {
          state.mapObject.removeLayer(polygonLayerId)
        }
        state.mapObject.addLayer({
          id: polygonLayerId,
          type: 'fill',
          source: polygonSource,
          paint: {
            'fill-opacity': datasetRoot.vizSettings.opacity || 0.8
          }
        })
        // Dedicated, user-styleable border line layer on top of the fill.
        const borderWidth = datasetRoot.vizSettings.borderWidth ?? 1
        if (borderWidth > 0) {
          state.mapObject.addLayer({
            id: polygonBorderId,
            type: 'line',
            source: polygonSource,
            paint: {
              'line-color': datasetRoot.vizSettings.borderColor || '#ffffff',
              'line-width': borderWidth
            }
          })
        }
        // For sublayer polygons, position them before the main dataset layer so they render underneath
        if (layerName === 'subLayer' && state.mapObject.getLayer('datasetPreview')) {
          state.mapObject.moveLayer(polygonLayerId, 'datasetPreview')
          if (state.mapObject.getLayer(polygonBorderId)) {
            state.mapObject.moveLayer(polygonBorderId, 'datasetPreview')
          }
        }
      } else if (datasetRoot.vizSettings.type === 'line') {
        state.mapObject.setLayoutProperty(layerName, 'visibility', 'none')
        if (state.mapObject.getLayer(polygonLayerId)) {
          state.mapObject.removeLayer(polygonLayerId)
        }
        if (!state.mapObject.getLayer(lineLayerId)) {
          state.mapObject.addLayer({
            id: lineLayerId,
            type: 'line',
            source: lineSource,
            paint: {
              'line-color': datasetRoot.vizSettings.circleColor || '#ffc107',
              'line-width': datasetRoot.vizSettings.lineWidth ?? 2,
              'line-opacity': datasetRoot.vizSettings.opacity ?? 0.8
            }
          })
        }
        state.mapObject.setLayoutProperty(lineLayerId, 'visibility', 'visible')
        state.mapObject.setPaintProperty(lineLayerId, 'line-opacity', datasetRoot.vizSettings.opacity ?? 0.8)
        if (layerName === 'subLayer' && state.mapObject.getLayer('datasetPreview')) {
          state.mapObject.moveLayer(lineLayerId, 'datasetPreview')
        }
      }
      // Color by setting. 'valueBased' with no header chosen yet (colorHeader
      // defaults to `false` until the user picks one) must fall back to the
      // solid color: building the value-based expression with a boolean
      // produces ['get', false], which Mapbox rejects ("Expected string but
      // found boolean") and the layer's color silently fails to update.
      if (datasetRoot.vizSettings.colorBy === 'solid' ||
        (datasetRoot.vizSettings.colorBy === 'valueBased' && !colorHeader)) {
        // Circle settings
        color = datasetRoot.vizSettings.circleColor
        if (datasetRoot.vizSettings.type === 'circle') {
          state.mapObject.setPaintProperty(layerName, 'circle-color', color)
        } else if (datasetRoot.vizSettings.type === 'polygon') {
          state.mapObject.setPaintProperty(polygonLayerId, 'fill-color', color)
        } else if (datasetRoot.vizSettings.type === 'line' && state.mapObject.getLayer(lineLayerId)) {
          state.mapObject.setPaintProperty(lineLayerId, 'line-color', color)
          state.mapObject.setPaintProperty(lineLayerId, 'line-width', datasetRoot.vizSettings.lineWidth ?? 2)
        }
      }
      // Value Based settings assume that the header is numbers
      if (datasetRoot.vizSettings.colorBy === 'valueBased' && colorHeader) {
        // Range includes the min and max for numbers as well as categories
        updatedDataset = state.dataset
        datasetRoot.vizSettings.minValue = false
        datasetRoot.vizSettings.maxValue = false
        datasetRoot.vizSettings.categorical = false
        datasetRoot.vizSettings.number = true
        if (!datasetRoot.vizSettings.colors) {
          datasetRoot.vizSettings.colors = [
            '#77CCCC',
            '#777711',
            '#AA7744',
            '#AAAA44',
            '#774411',
            '#4477AA',
            '#AA4455',
            '#771155',
            '#DDDD77',
            '#117744',
            '#DDAA77',
            '#CC99BB',
            '#44AAAA',
            '#117777',
            '#77AADD',
            '#88CCAA',
            '#DD7788',
            '#114477',
            '#AA4488',
            '#771122',
            '#44AA77'
          ]
        }
        geoJsonRoot.features.forEach((feature, i) => {
          let featureValue = feature.properties[colorHeader]
          // If null, then make the value 0
          if (featureValue === null || !featureValue) {
            featureValue = 0
            geoJsonRoot.features[i].properties[colorHeader] = 0
          }
          // Check to see if the property can be considered a number
          const notNumber = isNaN(featureValue)
          if (notNumber) {
            // If any feature is not a number, set categorical to true
            datasetRoot.vizSettings.categorical = true
            datasetRoot.vizSettings.number = false
          } else {
            // It is a number, now check to see if the property is stored
            // as a string and if so, convert to number
            if (typeof featureValue === 'string') {
              featureValue = Number(featureValue)
              const updatedDatasetGeoJson = geoJsonRoot
              updatedDatasetGeoJson.features[i].properties[colorHeader] = featureValue
              if (layerName === 'subLayer') {
                commit('setStateProperty', { property: 'subLayerGeoJson', value: updatedDatasetGeoJson })
              } else {
                commit('setStateProperty', { property: 'datasetGeoJson', value: updatedDatasetGeoJson })
              }
            }
            if (datasetRoot.vizSettings.minValue === false) {
              datasetRoot.vizSettings.minValue = featureValue
              datasetRoot.vizSettings.maxValue = featureValue
            }
            if (featureValue < datasetRoot.vizSettings.minValue) {
              datasetRoot.vizSettings.minValue = featureValue
            }
            if (featureValue > datasetRoot.vizSettings.maxValue) {
              datasetRoot.vizSettings.maxValue = featureValue
            }
          }
        })
        // Check to see if we have a filter for the primary colorHeader
        if (datasetRoot.vizSettings.filters.length > 0) {
          datasetRoot.vizSettings.filters.forEach((filter, index) => {
            if (filter.header === datasetRoot.vizSettings.colorHeader) {
              datasetRoot.vizSettings.minValue = datasetRoot.vizSettings.filters[index].min
              datasetRoot.vizSettings.maxValue = datasetRoot.vizSettings.filters[index].max
            }
          })
        }
        // Deal with Categories
        const categoryCounts = {}
        let categoryValue
        for (let i = 0; i < geoJsonRoot.features.length; i++) {
          categoryValue = geoJsonRoot.features[i].properties[colorHeader]
          if (typeof categoryCounts[categoryValue] === 'undefined') {
            categoryCounts[categoryValue] = 1
          } else {
            categoryCounts[categoryValue]++
          }
        }
        const entries = Object.entries(categoryCounts)
        entries.forEach((entry, i) => {
          if (i < 20) {
            if (entry[0] === '0' && datasetRoot.vizSettings.categorical) {
              datasetRoot.vizSettings.featureCategories.push({
                name: 'No Value',
                count: entry[1],
                color: '#333'
              })
            } else {
              datasetRoot.vizSettings.featureCategories.push({
                name: entry[0],
                count: entry[1],
                color: datasetRoot.vizSettings.colors[i]
              })
            }
          }
        })
        // Commit updated dataset to keep things in sync in store
        commit('setStateProperty', { property: 'dataset', value: updatedDataset })
        color = [
          'match',
          ['get', colorHeader]
        ]
        datasetRoot.vizSettings.featureCategories.forEach((category, i) => {
          let name = category.name
          if (datasetRoot.vizSettings.number) {
            name = Number(category.name)
          }
          color.push(name)
          color.push(category.color)
        })
        color.push('#333')
        // ZONE SETTINGS
        // LINEAR
        if (datasetRoot.vizSettings.zoneClassification === 'linear' && !datasetRoot.vizSettings.categorical) {
          color = [
            'interpolate',
            ['linear'],
            ['to-number', ['get', colorHeader]],
            datasetRoot.vizSettings.minValue,
            datasetRoot.vizSettings.minColor,
            datasetRoot.vizSettings.maxValue,
            datasetRoot.vizSettings.maxColor
          ]
        }
        // EQUAL INTERVAL
        if (datasetRoot.vizSettings.zoneClassification === 'equal-interval' && !datasetRoot.vizSettings.categorical) {
          console.log('Equal Interval')
          color = [
            'step',
            ['to-number', ['get', colorHeader]],
            'black',
            datasetRoot.vizSettings.minValue, datasetRoot.vizSettings.colors[0]
          ]
          const stopDiff = datasetRoot.vizSettings.maxValue - datasetRoot.vizSettings.minValue
          const stopLength = 1 / datasetRoot.vizSettings.zoneStops
          for (let i = 1; i < datasetRoot.vizSettings.zoneStops; i++) {
            const stopMultiplier = stopLength * i
            const stopFormula = (stopDiff * stopMultiplier) + datasetRoot.vizSettings.minValue
            color.push(stopFormula)
            color.push(datasetRoot.vizSettings.colors[i])
          }
        }
        // JENKS
        if (datasetRoot.vizSettings.zoneClassification === 'jenks' && !datasetRoot.vizSettings.categorical) {
          if (datasetRoot.vizSettings.recalculateJenks) {
            // Deal with Filters to limit the geoJson
            const filteredGeoJson = {
              type: 'FeatureCollection',
              features: []
            }
            geoJsonRoot.features.forEach(feature => {
              if (datasetRoot.vizSettings.filters.length > 0) {
                let passesFilters = true
                datasetRoot.vizSettings.filters.forEach(filter => {
                  if (feature.properties[filter.header] < filter.min || feature.properties[filter.header] > filter.max) {
                    passesFilters = false
                  }
                })
                if (passesFilters) {
                  filteredGeoJson.features.push(feature)
                }
              } else {
                filteredGeoJson.features.push(feature)
              }
            })
            updatedDataset = state.dataset
            datasetRoot.vizSettings.breaks = jenks(filteredGeoJson, colorHeader, datasetRoot.vizSettings.zoneStops)
            commit('setStateProperty', { property: 'dataset', value: updatedDataset })
          }
          if (datasetRoot.vizSettings.breaks[0] === undefined) {
            updatedDataset = state.dataset
            datasetRoot.vizSettings.breaks[0] = datasetRoot.vizSettings.minValue
            commit('setStateProperty', { property: 'dataset', value: updatedDataset })
          }
          datasetRoot.vizSettings.breaks.forEach((breakItem, i) => {
            if (typeof breakItem === 'string') {
              updatedDataset = state.dataset
              datasetRoot.vizSettings.breaks[i] = Number(datasetRoot.vizSettings.breaks[i])
              commit('setStateProperty', { property: 'dataset', value: updatedDataset })
            }
          })
          color = [
            'step',
            ['to-number', ['get', colorHeader]],
            'black'
          ]
          datasetRoot.vizSettings.breaks.forEach((breakItem, i) => {
            // Don't push the last 2 values because they only show the absolute last value of the breaks
            if (i < datasetRoot.vizSettings.breaks.length - 1) {
              color.push(datasetRoot.vizSettings.breaks[i])
              color.push(datasetRoot.vizSettings.colors[i])
            }
          })
        }
        // CATEGORICAL NUMBERS
        if (datasetRoot.vizSettings.zoneClassification === 'categorical') {
          updatedDataset = state.dataset
          datasetRoot.vizSettings.categorical = true
          commit('setStateProperty', { property: 'dataset', value: updatedDataset })
        }
        // }
      }
      // Check for sizing options
      if (datasetRoot.vizSettings.sizeBy === 'fixed') {
        radius = datasetRoot.vizSettings.circleRadius
      } else {
        updatedDataset = state.dataset
        datasetRoot.vizSettings.minSizeValue = false
        datasetRoot.vizSettings.maxSizeValue = false
        datasetRoot.vizSettings.sizeCategorical = false
        datasetRoot.vizSettings.sizeNumber = true
        commit('setStateProperty', { property: 'dataset', value: updatedDataset })
        // loop through and get min/max size values
        const sizeHeader = datasetRoot.vizSettings.sizeHeader
        geoJsonRoot.features.forEach((feature, i) => {
          let featureSizeValue = feature.properties[sizeHeader]
          console.log(featureSizeValue)
          // Check to see if the property can be considered a number
          const notNumber = isNaN(featureSizeValue)
          if (notNumber) {
            console.log('Not a number')
            // If any feature is not a number, set categorical to true
            updatedDataset = state.dataset
            datasetRoot.vizSettings.sizeCategorical = true
            datasetRoot.vizSettings.sizeNumber = false
            commit('setStateProperty', { property: 'dataset', value: updatedDataset })
          } else {
            console.log('Is a number')
            // It is a number, now check to see if the property is stored
            // as a string and if so, convert to number
            if (typeof featureSizeValue === 'string') {
              featureSizeValue = Number(featureSizeValue)
              const updatedDatasetGeoJson = geoJsonRoot
              updatedDatasetGeoJson.features[i].properties[sizeHeader] = featureSizeValue
              if (layerName === 'subLayer') {
                commit('setStateProperty', { property: 'subLayerGeoJson', value: updatedDatasetGeoJson })
              } else {
                commit('setStateProperty', { property: 'datasetGeoJson', value: updatedDatasetGeoJson })
              }
            }
            if (!datasetRoot.vizSettings.minSizeValue) {
              updatedDataset = state.dataset
              datasetRoot.vizSettings.minSizeValue = featureSizeValue
              datasetRoot.vizSettings.maxSizeValue = featureSizeValue
            }
            if (featureSizeValue < datasetRoot.vizSettings.minSizeValue) {
              datasetRoot.vizSettings.minSizeValue = featureSizeValue
            }
            if (featureSizeValue > datasetRoot.vizSettings.maxSizeValue) {
              datasetRoot.vizSettings.maxSizeValue = featureSizeValue
              console.log('Greater than!')
              console.log(featureSizeValue)
            }
            commit('setStateProperty', { property: 'dataset', value: updatedDataset })
          }
        })
        if (datasetRoot.vizSettings.sizeNumber) {
          console.log(datasetRoot)
          // Set up the radius setting
          radius = [
            'interpolate',
            ['linear'],
            ['to-number', ['get', datasetRoot.vizSettings.sizeHeader]],
            datasetRoot.vizSettings.minSizeValue, // Min Value
            datasetRoot.vizSettings.circleRadiusMin, // Min Render
            datasetRoot.vizSettings.maxSizeValue, // Max Value
            datasetRoot.vizSettings.circleRadiusMax // Max Render
          ]
        } else {
          radius = datasetRoot.vizSettings.circleRadius
        }
      }
      const filters = buildFilterExpression(datasetRoot.vizSettings.filters)
      if (filters) {
        // Number/date filters coerce with ['to-number'] (handles numeric
        // strings like "+16.1"); categorical filters keep only the selected
        // values. See store/filterExpression.js.
        state.mapObject.setFilter(layerName, filters)
        if (datasetRoot.vizSettings.type === 'polygon' && state.mapObject.getLayer(polygonLayerId)) {
          state.mapObject.setFilter(polygonLayerId, filters)
        }
        if (datasetRoot.vizSettings.type === 'line' && state.mapObject.getLayer(lineLayerId)) {
          state.mapObject.setFilter(lineLayerId, filters)
        }
      } else {
        state.mapObject.setFilter(layerName, undefined)
        if (datasetRoot.vizSettings.type === 'polygon' && state.mapObject.getLayer(polygonLayerId)) {
          state.mapObject.setFilter(polygonLayerId, undefined)
        }
        if (datasetRoot.vizSettings.type === 'line' && state.mapObject.getLayer(lineLayerId)) {
          state.mapObject.setFilter(lineLayerId, undefined)
        }
      }
      state.mapObject.setPaintProperty(layerName, 'circle-radius', radius)
      state.mapObject.setPaintProperty(layerName, 'circle-color', color)
      if (datasetRoot.vizSettings.type === 'polygon' && state.mapObject.getLayer(polygonLayerId)) {
        state.mapObject.setPaintProperty(polygonLayerId, 'fill-color', color)
      }
      if (datasetRoot.vizSettings.type === 'line' && state.mapObject.getLayer(lineLayerId)) {
        state.mapObject.setPaintProperty(lineLayerId, 'line-color', color)
        state.mapObject.setPaintProperty(lineLayerId, 'line-width', datasetRoot.vizSettings.lineWidth ?? 2)
      }
    }
    // HEAT MAP SETTINGS
    if (datasetRoot.vizSettings.type === 'heatMap') {
      let paintSettings
      // Weighted settings assume that the header is numbers
      if (datasetRoot.vizSettings.heatBy === 'weighted') {
        updatedDataset = state.dataset
        datasetRoot.vizSettings.minValue = false
        datasetRoot.vizSettings.maxValue = false
        datasetRoot.vizSettings.categorical = false
        datasetRoot.vizSettings.number = true
        commit('setStateProperty', { property: 'dataset', value: updatedDataset })
        geoJsonRoot.features.forEach(feature => {
          let featureValue = feature.properties[colorHeader]
          // Check to see if the property can be considered a number
          const notNumber = isNaN(featureValue)
          if (notNumber) {
            // If any feature is not a number, set categorical to true
            updatedDataset = state.dataset
            datasetRoot.vizSettings.categorical = true
            datasetRoot.vizSettings.number = false
            commit('setStateProperty', { property: 'dataset', value: updatedDataset })
          } else {
            // It is a number, now check to see if the property is stored
            // as a string and if so, convert to number
            if (typeof featureValue === 'string') {
              featureValue = Number(featureValue)
            }
            if (datasetRoot.vizSettings.minValue === false) {
              updatedDataset = state.dataset
              datasetRoot.vizSettings.minValue = featureValue
              datasetRoot.vizSettings.maxValue = featureValue
              commit('setStateProperty', { property: 'dataset', value: updatedDataset })
            }
            if (featureValue < datasetRoot.vizSettings.minValue) {
              updatedDataset = state.dataset
              datasetRoot.vizSettings.minValue = featureValue
              commit('setStateProperty', { property: 'dataset', value: updatedDataset })
            }
            if (featureValue > datasetRoot.vizSettings.maxValue) {
              updatedDataset = state.dataset
              datasetRoot.vizSettings.maxValue = featureValue
              commit('setStateProperty', { property: 'dataset', value: updatedDataset })
            }
          }
        })
        if (datasetRoot.vizSettings.number) {
          paintSettings = {
            'heatmap-weight': [
              'interpolate',
              ['linear'],
              ['to-number', ['get', colorHeader]],
              datasetRoot.vizSettings.minValue, // Min Value
              0, // Min Render
              datasetRoot.vizSettings.maxValue, // Max Value
              1 // Max Render
            ],
            'heatmap-radius': datasetRoot.vizSettings.heatRadius
          }
        } else {
          paintSettings = { 'heatmap-radius': datasetRoot.vizSettings.heatRadius }
        }
      } else {
        paintSettings = { 'heatmap-radius': datasetRoot.vizSettings.heatRadius }
      }
      state.mapObject.setLayoutProperty(layerName, 'visibility', 'none')
      if (!state.mapObject.getLayer('datasetPreviewHeatMap')) {
        state.mapObject.addLayer({
          id: 'datasetPreviewHeatMap',
          type: 'heatmap',
          source: 'datasetPreviewGeojson',
          // 'layout': {},
          paint: paintSettings
        })
      }
      const heatFilters = buildFilterExpression(datasetRoot.vizSettings.filters)
      state.mapObject.setFilter('datasetPreviewHeatMap', heatFilters || undefined)
    }
    // LABELS
    if (state.mapObject.getLayer('datasetPreviewLabels')) {
      state.mapObject.removeLayer('datasetPreviewLabels')
    }
    if (datasetRoot.vizSettings.labelBy === 'headerValue' && datasetRoot.vizSettings.labelHeader) {
      const isLineLayer = datasetRoot.vizSettings.type === 'line'
      const labelColor = datasetRoot.vizSettings.labelColor || '#ffffff'
      const labelSize = datasetRoot.vizSettings.labelSize ?? 14
      state.mapObject.addLayer({
        id: 'datasetPreviewLabels',
        type: 'symbol',
        source: 'datasetPreviewGeojson',
        layout: {
          'text-field': ['get', datasetRoot.vizSettings.labelHeader],
          'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
          'text-radial-offset': isLineLayer ? 0 : 0.8,
          'text-justify': 'auto',
          // Lines follow the path; points and polygons label at the
          // feature anchor (polygon labels land on the centroid).
          'symbol-placement': isLineLayer ? 'line' : 'point',
          'text-size': labelSize,
          'text-font': [
            'Open Sans Semibold',
            'Arial Unicode MS Bold'
          ]
        },
        paint: {
          'text-color': labelColor,
          // A faint contrasting halo keeps labels legible over any fill
          // colour without needing a second user control.
          'text-halo-color': 'rgba(0, 0, 0, 0.6)',
          'text-halo-width': 1
        }
      })
    }
    updatedDataset = state.dataset
    datasetRoot.vizSettings.recalculateJenks = false
    datasetRoot.vizSettings.recalculateRange = false
    commit('setStateProperty', { property: 'dataset', value: updatedDataset })
    commit('setStateProperty', { property: 'showRampSettings', value: true })
    commit('resetKey', 'dataset')

    if (state.farm.vizSettings && state.dataset.vizSettings.showBlocks) {
      // Show the blocks layer if they exist
      if (state.mapObject.getLayer('blocksLayer')) {
        state.mapObject.setLayoutProperty('blocksLayer', 'visibility', 'visible')
        state.mapObject.setLayoutProperty('blocksLayerOutline', 'visibility', 'visible')
        if (state.mapObject.getLayer('blocksLabelsLayer')) {
          state.mapObject.setLayoutProperty('blocksLabelsLayer', 'visibility', 'visible')
        }
      } else {
        // Blocks don't exist, create them
        dispatch('addBlocksLayer')
      }
    } else {
      // Hide the blocks layer if they exist
      if (state.mapObject.getLayer('blocksLayer')) {
        state.mapObject.setLayoutProperty('blocksLayer', 'visibility', 'none')
        state.mapObject.setLayoutProperty('blocksLayerOutline', 'visibility', 'none')
        if (state.mapObject.getLayer('blocksLabelsLayer')) {
          state.mapObject.setLayoutProperty('blocksLabelsLayer', 'visibility', 'none')
        }
      }
    }
    // Ensure sublayer polygon is positioned below the main dataset layer
    if (layerName !== 'subLayer' && state.mapObject.getLayer('subLayerPolygon') && state.mapObject.getLayer('datasetPreview')) {
      state.mapObject.moveLayer('subLayerPolygon', 'datasetPreview')
    }
  },
  // =========================================================================
  // Live tracking (Phase 1 — docs/live-tracking-phase1.md). Read-only
  // listeners over the tracks/lines stream the Collector app writes while a
  // vehicle is tracking; renders a tail polyline + labeled head marker per
  // in-progress track. No camera moves from snapshot handlers.
  // =========================================================================
  subscribeLiveTracks ({ commit, state, dispatch }) {
    dispatch('unsubscribeLiveTracks')
    const farmId = state.farm.id
    if (!farmId) return
    commit('setStateProperty', { property: 'liveTracksVisible', value: true })
    liveTracksUnsub = fb.tracksCollection
      .where('farmId', '==', farmId)
      .where('inProgress', '==', true)
      .onSnapshot(snap => {
        const present = {}
        snap.forEach(doc => {
          present[doc.id] = true
          const t = doc.data()
          const prev = liveTracksData[doc.id] || { segments: {}, lastPointAt: null }
          prev.label = t.createdByLabel || 'Tracking'
          prev.startedAt = t.startedAt && t.startedAt.toDate ? t.startedAt.toDate() : null
          liveTracksData[doc.id] = prev
          if (!liveLinesUnsubs[doc.id]) {
            // The farmId filter is REQUIRED for permissions, not just scoping:
            // the lines read rule resolves the farm role from
            // resource.data.farmId, and Firestore proves rules against the
            // QUERY, not per-document — an unfiltered subcollection listener
            // leaves farmId unconstrained and is rejected wholesale for every
            // non-admin. (Super admins bypass rules, which is how this
            // slipped past initial verification.) Chunk docs carry farmId.
            liveLinesUnsubs[doc.id] = fb.tracksCollection.doc(doc.id).collection('lines')
              .where('farmId', '==', farmId)
              .onSnapshot(linesSnap => {
                // Chunks can arrive out of order (offline retries set() on
                // deterministic IDs) — always rebuild sorted by
                // (segmentIndex, chunkIndex), mirroring trackFinalize.js.
                const chunks = linesSnap.docs.map(d => d.data())
                chunks.sort((a, b) =>
                  ((a.segmentIndex || 0) - (b.segmentIndex || 0)) ||
                  ((a.chunkIndex || 0) - (b.chunkIndex || 0)))
                const segments = {}
                let lastPointAt = null
                chunks.forEach(chunk => {
                  const si = chunk.segmentIndex || 0
                  if (!segments[si]) segments[si] = []
                  const lngs = Array.isArray(chunk.lngs) ? chunk.lngs : []
                  const lats = Array.isArray(chunk.lats) ? chunk.lats : []
                  const times = Array.isArray(chunk.times) ? chunk.times : []
                  const n = Math.min(lngs.length, lats.length)
                  for (let i = 0; i < n; i++) {
                    if (typeof lngs[i] !== 'number' || typeof lats[i] !== 'number' ||
                        isNaN(lngs[i]) || isNaN(lats[i])) continue
                    segments[si].push([lngs[i], lats[i]])
                    if (typeof times[i] === 'number' && times[i] > (lastPointAt || 0)) {
                      lastPointAt = times[i]
                    }
                  }
                })
                if (!liveTracksData[doc.id]) return // detached mid-flight
                liveTracksData[doc.id].segments = segments
                liveTracksData[doc.id].lastPointAt = lastPointAt
                dispatch('renderLiveTracks')
              }, err => console.log('live lines listener error:', err && err.message))
          }
        })
        // Tracks no longer in-progress (stopped/finalized): drop listener +
        // tail. The finalize pipeline files a coverage dataset through the
        // normal dataset flow, so nothing else is needed here.
        Object.keys(liveLinesUnsubs).forEach(id => {
          if (!present[id]) {
            liveLinesUnsubs[id]()
            delete liveLinesUnsubs[id]
            delete liveTracksData[id]
          }
        })
        dispatch('renderLiveTracks')
      }, err => console.log('live tracks listener error:', err && err.message))
  },

  unsubscribeLiveTracks ({ commit, state }) {
    if (liveTracksUnsub) {
      liveTracksUnsub()
      liveTracksUnsub = null
    }
    Object.keys(liveLinesUnsubs).forEach(id => liveLinesUnsubs[id]())
    liveLinesUnsubs = {}
    liveTracksData = {}
    if (liveRenderTimer) {
      clearTimeout(liveRenderTimer)
      liveRenderTimer = null
    }
    commit('setStateProperty', { property: 'liveTracksVisible', value: false })
    const m = state.mapObject
    if (m && m.getLayer) {
      ;['liveTrackLabels', 'liveTrackHeads', 'liveTrackTails'].forEach(layerId => {
        if (m.getLayer(layerId)) m.removeLayer(layerId)
      })
      if (m.getSource && m.getSource('liveTracksSource')) m.removeSource('liveTracksSource')
    }
  },

  renderLiveTracks ({ state }) {
    if (liveRenderTimer) return
    liveRenderTimer = setTimeout(() => {
      liveRenderTimer = null
      const m = state.mapObject
      if (!m || !m.getSource || !state.liveTracksVisible) return
      const MAX_TAIL_POINTS = 2000
      const STALE_MS = 10 * 60 * 1000
      const features = []
      Object.keys(liveTracksData).forEach(trackId => {
        const t = liveTracksData[trackId]
        let segs = Object.keys(t.segments || {})
          .sort((a, b) => Number(a) - Number(b))
          .map(k => t.segments[k])
          .filter(coords => coords.length > 0)
        // Cap the rendered tail at the most recent points, trimming whole
        // early points — never bridging pause/resume segment gaps.
        let total = segs.reduce((sum, c) => sum + c.length, 0)
        while (total > MAX_TAIL_POINTS && segs.length) {
          const excess = total - MAX_TAIL_POINTS
          if (segs[0].length <= excess) {
            total -= segs[0].length
            segs = segs.slice(1)
          } else {
            segs[0] = segs[0].slice(excess)
            total -= excess
          }
        }
        if (!segs.length) return
        const stale = t.lastPointAt ? (Date.now() - t.lastPointAt) > STALE_MS : false
        const lineSegs = segs.filter(c => c.length >= 2)
        if (lineSegs.length) {
          features.push({
            type: 'Feature',
            properties: { trackId, stale },
            geometry: { type: 'MultiLineString', coordinates: lineSegs }
          })
        }
        const lastSeg = segs[segs.length - 1]
        const head = lastSeg[lastSeg.length - 1]
        let elapsed = ''
        if (t.startedAt) {
          const mins = Math.max(0, Math.round((Date.now() - t.startedAt.getTime()) / 60000))
          elapsed = mins >= 60 ? ` · ${Math.floor(mins / 60)}h ${mins % 60}m` : ` · ${mins}m`
        }
        features.push({
          type: 'Feature',
          properties: {
            trackId,
            stale,
            head: true,
            label: (t.label || 'Tracking') + elapsed + (stale ? ' (stale)' : '')
          },
          geometry: { type: 'Point', coordinates: head }
        })
      })
      const fc = { type: 'FeatureCollection', features }
      const src = m.getSource('liveTracksSource')
      if (src) {
        src.setData(fc)
        return
      }
      m.addSource('liveTracksSource', { type: 'geojson', data: fc })
      m.addLayer({
        id: 'liveTrackTails',
        type: 'line',
        source: 'liveTracksSource',
        filter: ['!', ['has', 'head']],
        paint: {
          'line-color': ['case', ['get', 'stale'], '#9aa0a6', '#ff6b35'],
          'line-width': 3,
          'line-opacity': 0.85
        }
      })
      m.addLayer({
        id: 'liveTrackHeads',
        type: 'circle',
        source: 'liveTracksSource',
        filter: ['has', 'head'],
        paint: {
          'circle-radius': 7,
          'circle-color': ['case', ['get', 'stale'], '#9aa0a6', '#ff6b35'],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 2
        }
      })
      m.addLayer({
        id: 'liveTrackLabels',
        type: 'symbol',
        source: 'liveTracksSource',
        filter: ['has', 'head'],
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 12,
          'text-offset': [0, -1.4],
          'text-anchor': 'bottom',
          'text-allow-overlap': true
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#000000',
          'text-halo-width': 1.2
        }
      })
    }, 1000)
  },

  // Header-pill count listener — count only, mounted alongside FarmMenu.
  subscribeLiveTrackCount ({ commit, state, dispatch }) {
    dispatch('unsubscribeLiveTrackCount')
    const farmId = state.farm.id
    if (!farmId) return
    liveCountUnsub = fb.tracksCollection
      .where('farmId', '==', farmId)
      .where('inProgress', '==', true)
      .onSnapshot(snap => {
        commit('setStateProperty', { property: 'liveTrackCount', value: snap.size })
      }, err => console.log('live count listener error:', err && err.message))
  },

  unsubscribeLiveTrackCount ({ commit }) {
    if (liveCountUnsub) {
      liveCountUnsub()
      liveCountUnsub = null
    }
    commit('setStateProperty', { property: 'liveTrackCount', value: 0 })
  },

  // Pill click: enable the layer for this session and fit once — the only
  // permitted camera move, and it is user-initiated.
  focusLiveTracks ({ state, dispatch }, attempt = 0) {
    if (!state.liveTracksVisible) dispatch('subscribeLiveTracks')
    setTimeout(() => {
      const coords = []
      Object.keys(liveTracksData).forEach(id => {
        const segs = liveTracksData[id].segments || {}
        Object.keys(segs).forEach(k => segs[k].forEach(c => coords.push(c)))
      })
      if (!coords.length || !state.mapObject || !state.mapObject.fitBounds) {
        // Line chunks (or the map itself) arrive a beat later; retry for ~8 s.
        if (attempt < 6) dispatch('focusLiveTracks', attempt + 1)
        return
      }
      let minX = 180
      let maxX = -180
      let minY = 90
      let maxY = -90
      coords.forEach(c => {
        minX = Math.min(minX, c[0])
        maxX = Math.max(maxX, c[0])
        minY = Math.min(minY, c[1])
        maxY = Math.max(maxY, c[1])
      })
      state.mapObject.fitBounds([[minX, minY], [maxX, maxY]], {
        padding: 100,
        animate: true,
        maxZoom: 16
      })
    }, 1200)
  },

  removeAllLayers ({ commit, state }) {
    // Live-tracking layers (docs/live-tracking-phase1.md) — layers BEFORE the
    // source, or Mapbox throws "source cannot be removed while layer is using
    // it" (same bug class fixed for subLayerSource in 8417922). Listeners are
    // left attached: if the layer is still enabled, the next snapshot render
    // recreates source + layers on top of whatever replaced them.
    if (state.mapObject.getLayer) {
      ;['liveTrackLabels', 'liveTrackHeads', 'liveTrackTails'].forEach(layerId => {
        if (state.mapObject.getLayer(layerId)) state.mapObject.removeLayer(layerId)
      })
      if (state.mapObject.getSource && state.mapObject.getSource('liveTracksSource')) {
        state.mapObject.removeSource('liveTracksSource')
      }
    }
    if (state.mapObject.getLayer('blocksLayer')) {
      // state.mapObject.removeLayer('blocksLayer')
      // state.mapObject.removeLayer('blocksLayerOutline')
      // state.mapObject.removeSource('blocksLayer')
      // state.mapObject.removeSource('blocksLayerOutline')
      state.mapObject.setLayoutProperty('blocksLayer', 'visibility', 'none')
      state.mapObject.setLayoutProperty('blocksLayerOutline', 'visibility', 'none')
    }
    if (state.mapObject.getLayer('blocksLabelsLayer')) {
      // state.mapObject.removeLayer('blocksLabelsLayer')
      // state.mapObject.removeSource('blocksLabelsLayer')
      state.mapObject.setLayoutProperty('blocksLabelsLayer', 'visibility', 'none')
    }
    if (state.mapObject.getLayer('blockHighlightLayer')) {
      state.mapObject.removeLayer('blockHighlightLayer')
    }
    if (state.mapObject.getLayer('blocksGreyedLayer')) {
      state.mapObject.removeLayer('blocksGreyedLayer')
      state.mapObject.removeSource('blocksGreyedLayer')
    }
    if (state.mapObject.getLayer('Footprint')) {
      state.mapObject.removeLayer('Footprint')
    }
    if (state.mapObject.getLayer('geojsonMapPreview')) {
      state.mapObject.removeLayer('geojsonMapPreview')
    }
    if (state.mapObject.getSource('geoJsonSource')) {
      state.mapObject.removeSource('geoJsonSource')
    }
    if (state.mapObject.getLayer('interpolation')) {
      state.mapObject.removeLayer('interpolation')
    }
    if (state.mapObject.getSource('interpolatedGeoJsonSource')) {
      state.mapObject.removeSource('interpolatedGeoJsonSource')
    }
    if (state.mapObject.getLayer('datasetPreview')) {
      state.mapObject.removeLayer('datasetPreview')
    }
    if (state.mapObject.getLayer('datasetPreviewLabels')) {
      state.mapObject.removeLayer('datasetPreviewLabels')
    }
    // Remove every layer that uses subLayerSource BEFORE the source itself —
    // Mapbox throws "Source subLayerSource cannot be removed while layer
    // subLayerLine is using it" if the source goes first (subLayer is a circle
    // layer, but the line/polygon layers share the same source).
    if (state.mapObject.getLayer('subLayer')) {
      state.mapObject.removeLayer('subLayer')
    }
    if (state.mapObject.getLayer('subLayerPolygonBorder')) {
      state.mapObject.removeLayer('subLayerPolygonBorder')
    }
    if (state.mapObject.getLayer('subLayerPolygon')) {
      state.mapObject.removeLayer('subLayerPolygon')
    }
    if (state.mapObject.getLayer('subLayerLine')) {
      state.mapObject.removeLayer('subLayerLine')
    }
    if (state.mapObject.getSource('subLayerSource')) {
      state.mapObject.removeSource('subLayerSource')
    }
    if (state.mapObject.getLayer('datasetPreviewHeatMap')) {
      state.mapObject.removeLayer('datasetPreviewHeatMap')
    }
    if (state.mapObject.getLayer('datasetPreviewPolygonBorder')) {
      state.mapObject.removeLayer('datasetPreviewPolygonBorder')
    }
    if (state.mapObject.getLayer('datasetPreviewPolygon')) {
      state.mapObject.removeLayer('datasetPreviewPolygon')
    }
    if (state.mapObject.getLayer('datasetPreviewLine')) {
      state.mapObject.removeLayer('datasetPreviewLine')
    }
    if (state.mapObject.getSource('datasetPreviewGeojson')) {
      state.mapObject.removeSource('datasetPreviewGeojson')
    }
    if (state.mapObject.getLayer('selectedDataPointHighlight')) {
      state.mapObject.removeLayer('selectedDataPointHighlight')
    }
    if (state.mapObject.getLayer('selectedDataPointLine')) {
      state.mapObject.removeLayer('selectedDataPointLine')
    }
    if (state.mapObject.getLayer('selectedDataPoint')) {
      state.mapObject.removeLayer('selectedDataPoint')
    }
    if (state.mapObject.getSource('selectedDataPoint')) {
      state.mapObject.removeSource('selectedDataPoint')
    }
    if (state.mapObject.getLayer('selectedDataPointPolygon')) {
      state.mapObject.removeLayer('selectedDataPointPolygon')
    }
    if (state.mapObject.getSource('selectedDataPointPolygon')) {
      state.mapObject.removeSource('selectedDataPointPolygon')
    }
    if (state.mapObject.getLayer('rasterLayer')) {
      state.mapObject.removeLayer('rasterLayer')
      state.mapObject.removeSource('rasterSource')
    }
  },
  /**
   * If the farm has a custom raster base layer configured, fetch a fresh
   * TiTiler tile URL from the backend (callable: getRasterTileSource) and
   * add it as the bottom-most map layer so blocks/datasets render on top.
   *
   * Idempotent: if the layer is already present and not stale, leaves it
   * in place. Stale = the signed URL has < 5 minutes left.
   *
   * No-ops cleanly when:
   *   - No farm in state (e.g. signed-in but not on a farm route).
   *   - farm.baseLayer is missing, null, or kind !== 'raster-dataset'.
   *   - The mapObject isn't ready yet.
   */
  async addCustomBaseLayer ({ state }) {
    try {
      if (!state.mapObject) return
      const farm = state.farm
      const cfg = farm && farm.baseLayer
      if (!cfg || cfg.kind !== 'raster-dataset' || !cfg.datasetId) {
        // Nothing to add — but tear down any leftover layer so a previous
        // farm's base raster doesn't bleed through if the user switched
        // farms or just disabled the feature.
        if (state.mapObject.getLayer('customBaseLayer')) {
          state.mapObject.removeLayer('customBaseLayer')
        }
        if (state.mapObject.getSource('customBaseLayerSource')) {
          state.mapObject.removeSource('customBaseLayerSource')
        }
        return
      }

      // Cache check: if we already have a non-stale tile URL for this dataset
      // and the layer is rendered, skip the round-trip.
      const cache = state.customBaseLayerCache || null
      const now = Date.now()
      // Backend mints 7-day signed URLs, so a 1-hour staleness threshold
      // gives us ~6 days of cache hits per generation while still protecting
      // against handing Mapbox a URL that's about to expire mid-session.
      const minTtlMs = 60 * 60 * 1000
      let tileSource = null
      if (cache && cache.datasetId === cfg.datasetId && cache.expiresAtMs - now > minTtlMs) {
        tileSource = cache
      } else {
        // We deliberately call the function over plain HTTPS with a Bearer
        // token rather than going through firebase.functions().httpsCallable.
        // The v7 callable SDK in this project's namespaced-firebase setup
        // throws a generic "internal" error before even making the network
        // request — likely an auth-token-resolution race that's never been
        // reproducible from outside. The function itself accepts the same
        // {data: {...}} envelope a callable would send, so a direct fetch
        // is fully equivalent and entirely sidesteps the broken SDK path.
        const user = firebase.auth().currentUser
        if (!user) throw new Error('Sign in required for custom base layer.')
        const idToken = await user.getIdToken()
        const url = 'https://us-central1-efficient-vineyard.cloudfunctions.net/getRasterTileSource'
        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + idToken
          },
          body: JSON.stringify({ data: { datasetId: cfg.datasetId } })
        })
        if (!resp.ok) {
          const errBody = await resp.text()
          throw new Error(`getRasterTileSource HTTP ${resp.status}: ${errBody.slice(0, 200)}`)
        }
        const json = await resp.json()
        const data = json.result || {}
        tileSource = {
          datasetId: cfg.datasetId,
          tileUrlTemplate: data.tileUrlTemplate,
          expiresAtMs: new Date(data.expiresAt).getTime(),
          bounds: data.bounds || null
        }
        // Stash on state for subsequent calls. Plain object on state — not
        // a Vuex mutation so the data doesn't end up in devtools history.
        state.customBaseLayerCache = tileSource
      }

      // Remove any existing custom-base layer so we re-apply with the
      // (possibly refreshed) URL. Ordering: blocks layer is added AFTER
      // this dispatch returns, so it lands on top automatically.
      if (state.mapObject.getLayer('customBaseLayer')) {
        state.mapObject.removeLayer('customBaseLayer')
      }
      if (state.mapObject.getSource('customBaseLayerSource')) {
        state.mapObject.removeSource('customBaseLayerSource')
      }

      const sourceConfig = {
        type: 'raster',
        tiles: [tileSource.tileUrlTemplate],
        tileSize: 256,
        // Hint to Mapbox so it doesn't request tiles outside the COG —
        // avoids a sea of 404s for tiles that aren't in the imagery.
        ...(tileSource.bounds ? { bounds: tileSource.bounds } : {})
      }
      state.mapObject.addSource('customBaseLayerSource', sourceConfig)
      // Insert the custom raster BELOW any existing block / dataset layers.
      // On first farm load, dispatch('addCustomBaseLayer') is fired before
      // blocks are added, but this action's network round-trip (signing the
      // tile URL) typically resolves AFTER blocks land — so without a
      // beforeId, the raster would render on top and hide the boundaries.
      // Walk the well-known map-layer ids in z-order (lowest to highest)
      // and insert just below the first one we find that already exists.
      const layersOnTopOfBase = [
        'blocksLayer', 'blocksLayerOutline', 'blocksLabelsLayer',
        'datasetPreview', 'datasetPreviewPolygon', 'datasetPreviewLine',
        'subLayer', 'subLayerPolygon', 'subLayerLine',
        'selectedDataPoint', 'selectedDataPointHighlight',
        'blockHighlightLayer'
      ]
      const beforeId = layersOnTopOfBase.find(id => state.mapObject.getLayer(id))
      state.mapObject.addLayer({
        id: 'customBaseLayer',
        type: 'raster',
        source: 'customBaseLayerSource',
        paint: {
          'raster-opacity': typeof cfg.opacity === 'number' ? cfg.opacity : 1
        }
      }, beforeId)
    } catch (err) {
      // A failed tile-source fetch shouldn't blow up the whole map render.
      // Most common failures: dataset deleted, user lost access, signed-URL
      // service hiccup. Fall through silently — the default basemap remains.
      // Log the full error object so the underlying server-side message is
      // visible in the console (Firebase httpsCallable wraps server errors
      // with `code` + `details` — `details` carries the real message).
      console.warn('addCustomBaseLayer skipped:', {
        code: err && err.code,
        message: err && err.message,
        details: err && err.details
      })
    }
  },

  addBlocksLayer ({ state, dispatch }) {
    // Check if layers already exist
    if (state.mapObject.getLayer('blocksLayer')) {
      // Layers exist, just make them visible
      state.mapObject.setLayoutProperty('blocksLayer', 'visibility', 'visible')
      state.mapObject.setLayoutProperty('blocksLayerOutline', 'visibility', 'visible')
      // Remove any existing click handlers
      state.mapObject.off('click', 'blocksLayer')
      state.mapObject.off('mouseenter', 'blocksLayer')
      state.mapObject.off('mouseleave', 'blocksLayer')
      if (state.mapObject.getLayer('blocksLabelsLayer')) {
        state.mapObject.setLayoutProperty('blocksLabelsLayer', 'visibility', 'visible')
      }
    } else {
      // Create new layers
      const plainGeoJson = JSON.parse(JSON.stringify(state.blocksGeoJson))
      
      // Sort features by area (largest first) so smaller blocks render on top
      // if (plainGeoJson.features && Array.isArray(plainGeoJson.features)) {
      //   plainGeoJson.features.sort((a, b) => {
      //     const areaA = this.calculatePolygonArea(a.geometry)
      //     const areaB = this.calculatePolygonArea(b.geometry)
      //     return areaB - areaA // Descending order (largest first)
      //   })
      //   console.log('Sorted blocks by area - largest to smallest for proper layering')
      // }
      
      state.mapObject.addSource('blocksLayer', {
        type: 'geojson',
        data: plainGeoJson
      })

      state.mapObject.addLayer({
        id: 'blocksLayer',
        type: 'fill',
        source: 'blocksLayer',
        layout: {
          visibility: 'visible'
        },
        paint: {
          'fill-color': state.farm.vizSettings?.blockColor || '#6e59c7',
          'fill-opacity': (state.farm.vizSettings?.colorOpacity ?? 0.8)
        }
      })

      state.mapObject.addLayer({
        id: 'blocksLayerOutline',
        type: 'line',
        source: 'blocksLayer',
        layout: {
          visibility: 'visible'
        },
        paint: {
          'line-width': state.farm.vizSettings?.lineWidth || 2,
          'line-color': state.farm.vizSettings?.lineColor || '#eee'
        }
      })
    }
    dispatch('initFarmViz')
  },

  /**
   * Append a newly saved datapoint to the in-memory dataset GeoJSON and update the map
   * so the new point/polygon appears immediately without recompiling. Used during collection.
   */
  appendDynamicDatapointToMap ({ state, commit, dispatch }, { dataset, dataPoint, dataPointId, collector }) {
    if (!dataset.dynamic || !state.mapObject) return
    const fields = (collector && collector.fields) || state.collector.fields || []
    let geometry = null
    if (dataPoint.geometry) {
      try {
        const geom = typeof dataPoint.geometry === 'string' ? JSON.parse(dataPoint.geometry) : dataPoint.geometry
        if (geom && (
          geom.type === 'Polygon' ||
          geom.type === 'MultiPolygon' ||
          geom.type === 'LineString' ||
          geom.type === 'MultiLineString'
        )) geometry = geom
      } catch (e) { /* ignore */ }
    }
    if (!geometry && dataPoint.geolocation) {
      const lat = dataPoint.geolocation.latitude ?? dataPoint.geolocation._lat
      const lng = dataPoint.geolocation.longitude ?? dataPoint.geolocation._long
      if (typeof lat === 'number' && typeof lng === 'number') {
        geometry = { type: 'Point', coordinates: [lng, lat] }
      }
    }
    if (!geometry) return
    const properties = {}
    fields.forEach((field) => {
      if (field.type === 'Date and Time') {
        const v = dataPoint[field.machine_name]
        if (v) properties[field.label] = (v._seconds != null) ? v._seconds : (v.seconds != null) ? v.seconds : Math.floor((v.getTime ? v.getTime() : new Date(v).getTime()) / 1000)
        else properties[field.label] = null
      } else if (field.type === 'Computer Vision') {
        const cvValue = dataPoint[field.machine_name]
        if (cvValue && cvValue[0]) {
          const classes = cvValue[0].classes || []
          classes.forEach((className) => {
            properties[field.label + ' - ' + className] = cvValue[0].classCounts?.[className] || 0
          })
        }
      } else {
        properties[field.label] = dataPoint[field.machine_name]
      }
    })
    const createdOn = dataPoint.createdOn
    const updatedOn = dataPoint.updatedOn
    const toSeconds = (v) => {
      if (v == null) return null
      if (typeof v === 'number') return v
      if (v._seconds != null) return v._seconds
      if (v.seconds != null) return v.seconds
      return Math.floor((v.getTime ? v.getTime() : new Date(v).getTime()) / 1000)
    }
    properties.createdOn = toSeconds(createdOn)
    properties.updatedOn = toSeconds(updatedOn)
    properties.dataPointId = dataPointId
    const newFeature = { type: 'Feature', properties, geometry }
    let geoJson = state.datasetGeoJson
    if (!geoJson || !geoJson.features) {
      geoJson = { type: 'FeatureCollection', features: [] }
    } else {
      geoJson = { type: 'FeatureCollection', features: [...geoJson.features] }
    }
    geoJson.features.push(newFeature)
    commit('setStateProperty', { property: 'datasetGeoJson', value: geoJson })
    const source = state.mapObject.getSource('datasetPreviewGeojson')
    if (source) source.setData(geoJson)
  },

  /**
   * After a datapoint's log (or any header) is updated, sync that datapoint into
   * state.datasetGeoJson and refresh the map so the dataset view displays correctly.
   */
  refreshDatasetGeoJsonFromDatapoint ({ state, commit }, { dataPointId, dataPointData }) {
    if (!state.datasetGeoJson || !state.datasetGeoJson.features || !dataPointId || !dataPointData) return
    const features = state.datasetGeoJson.features
    let updated = false
    for (let i = 0; i < features.length; i++) {
      const f = features[i]
      if (f.properties && f.properties.dataPointId === dataPointId) {
        f.properties = { dataPointId, ...dataPointData }
        updated = true
        break
      }
    }
    if (updated && state.mapObject) {
      commit('setStateProperty', { property: 'datasetGeoJson', value: state.datasetGeoJson })
      const source = state.mapObject.getSource('datasetPreviewGeojson')
      if (source) source.setData(JSON.parse(JSON.stringify(state.datasetGeoJson)))
    }
  },

  /**
   * Calculate the area of a polygon geometry
   * Uses the Shoelace formula for polygon area calculation
   */
  calculatePolygonArea (geometry) {
    if (!geometry || !geometry.coordinates || !geometry.coordinates[0]) {
      return 0
    }
    
    const coords = geometry.coordinates[0] // Outer ring
    let area = 0
    
    // Shoelace formula for polygon area
    for (let i = 0; i < coords.length - 1; i++) {
      const [x1, y1] = coords[i]
      const [x2, y2] = coords[i + 1]
      area += (x1 * y2) - (x2 * y1)
    }
    
    return Math.abs(area / 2)
  }
}
